import { getStorageStretchErrors } from '../transforms/storage-stretch.transform';
import { isRetainedSonOwner } from '../transforms/son-ownership.transform';
import { VCPM } from '../models/project.model';
import { WORKLOAD_DOMAIN_TYPE } from '../models/pcsizer.enums';
import { FieldValidation, ValidationResult, WorkloadValidationResult } from '../models/validation-result.model';

/**
 * Ported as-is from WorkloadDomainSizingComponent.isRequestValid
 * (workload-domain-sizing.component.ts:797-846).
 */
export function validateWorkloadDomains(project: VCPM.Project): WorkloadValidationResult {
  let workloadDomainReturnObj: FieldValidation | undefined;
  let compReturnObj: FieldValidation | undefined;
  let workloadReturnObj: FieldValidation | undefined;

  const wlds: VCPM.WorkloadDomain[] =
    project && project.data && project.data.workloadDomains ? project.data.workloadDomains : [];

  if (!(wlds && Array.isArray(wlds) && wlds.length > 0)) {
    workloadDomainReturnObj = {
      sizing: false,
      errorStr: `Workload Domains are not configured!`,
    };
  }

  for (let index = 0; index < wlds.length; index++) {
    const element = wlds[index];
    if (isRetainedSonOwner(project.data, element)) continue;
    // Check for S/W Components
    if (
      !(
        element.selectedComponents &&
        element.selectedComponents.swComponents &&
        Array.isArray(element.selectedComponents.swComponents) &&
        element.selectedComponents.swComponents.length > 1
      )
    ) {
      compReturnObj = {
        sizing: false,
        errorStr: `${element.domainName}'s components are not selected!`,
      };
    }

    if (
      !(
        element.workloadList &&
        Array.isArray(element.workloadList) &&
        element.workloadList.length
      )
    ) {
      workloadReturnObj = {
        sizing: false,
        errorStr: `Cannot Proceed: Please configure one or more workloads to ${element.domainName} to proceed further.`,
      };
    }
  }

  const storageErrors = getStorageStretchErrors(project?.data);
  if (storageErrors.length) workloadReturnObj = { sizing: false, errorStr: storageErrors.join(' ') };

  return {
    workloadDomain: workloadDomainReturnObj,
    components: compReturnObj,
    workload: workloadReturnObj,
  };
}

export interface WorkloadDomainTypeCount {
  domainType: WORKLOAD_DOMAIN_TYPE;
}

/**
 * Ported as-is from WorkloadDomainSizingComponent.openAddDomainDialog's inline
 * limit check (workload-domain-sizing.component.ts:541-552) plus
 * countOfWorkloadDomains (workload-domain-sizing.component.ts:556-562).
 *
 * The real hidden input is the component's `listOfDomains` (an in-memory list
 * that mirrors project.data.workloadDomains plus any domains added but not yet
 * saved in the current dialog session) - not VCPM.Project directly. Callers
 * pass that list in.
 */
export function checkWorkloadDomainLimits(domains: WorkloadDomainTypeCount[]): ValidationResult {
  const counts = domains.reduce((acc, curr) => {
    acc[curr.domainType] = (acc[curr.domainType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const standardCount = counts[WORKLOAD_DOMAIN_TYPE.Standard] || 0;
  const isolatedCount = counts[WORKLOAD_DOMAIN_TYPE.Isolated] || 0;

  if (standardCount + isolatedCount >= 24 || isolatedCount >= 24) {
    return {
      valid: false,
      errors: ['Maximum Workload Domains Reached (Standard + Isolated).'],
    };
  }
  if (standardCount >= 14) {
    return {
      valid: false,
      errors: [
        'Maximum Standard Workload Domains Reached, Please add Isolated Workload Domains if required to deploy more workload domains.',
      ],
    };
  }
  return { valid: true, errors: [] };
}
